import Toolbar from '@mui/material/Toolbar';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import MenuIcon from '@mui/icons-material/Menu';
import { AutoAwesome, AutoAwesomeOutlined, AccountCircle, Palette } from '@mui/icons-material';
import GlobalSearch from './GlobalSearch';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/users.slice';
import { authClient } from '../../lib/auth-client';
import { Link, useLocation } from 'react-router-dom';
import { RootState } from '../../store';
import { useAppTheme, ACCENT_PALETTE } from '../../context/ThemeContext';
import Logo from '../../assets/images/logo.svg';

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  borderRadius: 0,
  width: "100%",
  left: 0,
  zIndex: theme.zIndex.drawer + 1,
}));

const PAGE_TITLES: Record<string, string> = {
  "": "Dashboard",
  dashboard: "Dashboard",
  tasks: "Tasks",
  users: "Users",
  submissions: "Submissions",
  students: "Students",
  transactions: "Transactions",
  analytics: "Analytics",
  invoices: "Invoices",
};

function Header({
  handleSidebar,
  sidebar,
}: {
  handleSidebar: () => void;
  sidebar: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [paletteAnchor, setPaletteAnchor] = useState<null | HTMLElement>(null);
  const username = useSelector((state: RootState) => state.user.username);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isNewTheme, toggleTheme, accentColor, setAccentColor } = useAppTheme();

  const segment = location.pathname.split("/")[1] || "";
  const title = PAGE_TITLES[segment] || segment;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const initials = username ? username.slice(0, 2).toUpperCase() : "U";

  return (
    <AppBar open={sidebar} elevation={0} position="fixed">
      <Toolbar disableGutters sx={{ height: 64 }}>

        {/* ── Brand section: same width as sidebar ── */}
        <Box
          component={Link}
          to="/"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
            height: "100%",
            textDecoration: "none",
            borderRight: isNewTheme
              ? "1px solid #E2E8F0"
              : "1px solid rgba(0,0,0,0.08)",
            backgroundColor: "transparent",
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: isNewTheme ? "rgba(15,23,42,0.03)" : "rgba(0,0,0,0.03)",
            },
          }}>
          <img src={Logo} alt="5milah" width={52} height={52} style={{ display: "block" }} />
        </Box>

        {/* ── Sidebar toggle ── */}
        <IconButton
          onClick={handleSidebar}
          size="medium"
          aria-label="toggle drawer"
          sx={{
            ml: 1.5,
            color: isNewTheme ? "text.primary" : "inherit",
            "&:hover": {
              backgroundColor: isNewTheme ? "rgba(15,23,42,0.06)" : undefined,
            },
          }}>
          <MenuIcon />
        </IconButton>

        {/* ── Divider + page title ── */}
        {isNewTheme ? (
          <>
            <Box
              sx={{
                width: "1px",
                height: 22,
                borderRadius: 999,
                backgroundColor: "#E2E8F0",
                mx: 1.5,
                flexShrink: 0,
                display: { xs: "none", sm: "block" },
              }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                display: { xs: "none", sm: "block" },
                textTransform: "capitalize",
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "0.01em",
              }}>
              {title}
            </Typography>
          </>
        ) : (
          <Typography
            variant="h6"
            noWrap
            sx={{
              ml: 1,
              display: { xs: "none", sm: "block" },
              textTransform: "capitalize",
              fontWeight: 600,
              color: "text.primary",
            }}>
            {title}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* ── Global search ── */}
        <Box sx={{ mr: 1.5 }}>
          <GlobalSearch />
        </Box>

        {/* ── Accent color picker (new theme only) ── */}
        {isNewTheme && (
          <>
            <Tooltip title="Accent color" arrow>
              <IconButton
                size="small"
                onClick={(e) => setPaletteAnchor(e.currentTarget)}
                sx={{
                  mr: 0.5,
                  width: 36,
                  height: 36,
                  color: accentColor,
                  backgroundColor: `${accentColor}14`,
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: `${accentColor}22` },
                }}>
                <Palette sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Popover
              open={Boolean(paletteAnchor)}
              anchorEl={paletteAnchor}
              onClose={() => setPaletteAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 0.75,
                  p: 1.5,
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 8px 32px rgba(15,23,42,0.10)",
                },
              }}>
              <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                Accent Color
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", maxWidth: 176 }}>
                {ACCENT_PALETTE.map(({ color, label }) => {
                  const isSelected = accentColor === color;
                  return (
                    <Tooltip key={color} title={label} arrow placement="top">
                      <Box
                        onClick={() => { setAccentColor(color); setPaletteAnchor(null); }}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          backgroundColor: color,
                          cursor: "pointer",
                          outline: isSelected ? `2px solid ${color}` : "2px solid transparent",
                          outlineOffset: "2px",
                          transition: "transform 0.15s ease, outline 0.15s ease",
                          "&:hover": { transform: "scale(1.2)" },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Popover>
          </>
        )}

        {/* ── Theme toggle ── */}
        <Tooltip title={isNewTheme ? "Switch to Classic" : "Switch to New Theme"} arrow>
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              mr: 1,
              color: isNewTheme ? accentColor : "action.active",
              backgroundColor: isNewTheme ? `${accentColor}14` : "rgba(0,0,0,0.04)",
              width: 36,
              height: 36,
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: isNewTheme ? `${accentColor}22` : "rgba(0,0,0,0.08)",
                transform: "rotate(15deg) scale(1.05)",
              },
            }}>
            {isNewTheme
              ? <AutoAwesome sx={{ fontSize: 18 }} />
              : <AutoAwesomeOutlined sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>

        {/* ── User menu ── */}
        {isNewTheme ? (
          <Chip
            avatar={
              <Avatar sx={{ bgcolor: "#0F172A", color: "#fff", fontWeight: 700, fontSize: "0.7rem", width: 28, height: 28 }}>
                {initials}
              </Avatar>
            }
            label={username || "User"}
            onClick={handleMenu}
            sx={{
              mr: 2,
              borderRadius: 4,
              fontWeight: 500,
              fontSize: "0.875rem",
              backgroundColor: "#F8FAFC",
              color: "#0F172A",
              border: "1px solid #E2E8F0",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "#F1F5F9",
                borderColor: "#CBD5E1",
              },
            }}
          />
        ) : (
          <IconButton
            size="large"
            edge="end"
            aria-haspopup="true"
            onClick={handleMenu}
            sx={{ mr: 1 }}>
            <AccountCircle />
          </IconButton>
        )}

        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          keepMounted
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            sx: isNewTheme
              ? { borderRadius: "8px", boxShadow: "0 8px 32px rgba(15,23,42,0.1)", border: "1px solid #E2E8F0", minWidth: 160, mt: 0.5 }
              : { mt: 0.5 },
          }}>
          <MenuItem
            onClick={async () => { await authClient.signOut(); dispatch(logout()); handleClose(); }}
            sx={{ fontWeight: isNewTheme ? 500 : 400, color: isNewTheme ? "#EF4444" : "inherit" }}>
            Logout
          </MenuItem>
          <Link to={"login"} style={{ textDecoration: "none", color: "unset" }}>
            <MenuItem sx={{ fontWeight: isNewTheme ? 600 : 400 }}>Change User</MenuItem>
          </Link>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
